import * as yup from 'yup';
import { useTranslate } from '@tolgee/react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import TextField from '@amf/shared/components/form/TextField';
import Button from '@amf/shared/components/button/Button';
import { TournamentCategoryItemFragment } from '@amf/shared/types/graphql';
import SelectField from '@amf/shared/components/form/SelectField';

const schema = yup.object({
  teamName: yup
    .string()
    .required('join.team.form.teamName.required')
    .max(64, 'join.team.form.teamName.max'),
  managerName: yup
    .string()
    .required('join.team.form.managerName.required')
    .max(64, 'join.team.form.managerName.max'),
  email: yup
    .string()
    .email('join.team.form.email.invalid')
    .required('join.team.form.email.required'),
  phone: yup
    .string()
    .matches(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/g, 'join.team.form.phone.invalid')
    .required('join.team.form.phone.required'),
  category: yup.string().required('join.team.form.category.required'),
});

export interface JoinTeamFormData {
  teamName: string;
  managerName: string;
  email: string;
  phone: string;
  category: string;
}

interface JoinTeamFormProps {
  categories: TournamentCategoryItemFragment[];
  values: JoinTeamFormData;
  onSubmit: (values: JoinTeamFormData) => Promise<void>;
}

function JoinTeamForm({ values, onSubmit, categories }: JoinTeamFormProps) {
  const { t } = useTranslate('general');
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [sending, setSending] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<JoinTeamFormData>({
    resolver: yupResolver(schema),
    defaultValues: values,
  });

  const verifyReCaptcha = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }

    await executeRecaptcha('contactForm');
  }, [executeRecaptcha]);

  useEffect(() => {
    verifyReCaptcha();
  }, [verifyReCaptcha]);

  const saveForm = useCallback(
    async (data: JoinTeamFormData) => {
      setSending(true);
      await onSubmit(data);
      setSending(false);
    },
    [onSubmit],
  );

  return (
    <div className='Form'>
      <form onSubmit={handleSubmit(saveForm)}>
        <div className='Form__row'>
          <div className='Form__col'>
            <Controller
              name='teamName'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.team.form.teamName')}
                  error={errors.teamName?.message}
                  {...field}
                />
              )}
            />
          </div>
          <div className='Form__col'>
            <Controller
              name='managerName'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.team.form.managerName')}
                  error={errors.managerName?.message}
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className='Form__row'>
          <div className='Form__col'>
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.team.form.email')}
                  error={errors.email?.message}
                  type='email'
                  {...field}
                />
              )}
            />
          </div>
          <div className='Form__col'>
            <Controller
              name='phone'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.team.form.phone')}
                  error={errors.phone?.message}
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className='Form__row'>
          <div className='Form__col'>
            <Controller
              name='category'
              control={control}
              render={({ field }) => (
                <SelectField
                  label={t('join.team.form.category')}
                  error={errors.category?.message}
                  className='Form__field'
                  {...field}
                >
                  <option value={''}>{t('join.team.form.category.choose')}</option>
                  {categories.map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </SelectField>
              )}
            />
          </div>
        </div>
        <div className='Form__row'>
          <div className='Form__col'>
            <Button type='submit' variant='dark-outline' disabled={sending}>
              {t('join.team.form.send')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default JoinTeamForm;
